import { useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { sendMemberUiEvent } from '../../lib/api/memberApi'
import { ActivitySheet } from './ActivitySheet'
import { AnnouncementPopup } from './AnnouncementPopup'
import { GiftBoxIcon } from './GiftBoxIcon'
import { GoldBagIcon } from './GoldBagIcon'
import { afterAnnouncements, announcementKey, giftWaiting, initialStage, readActivities, readAnnouncements, unshownAnnouncements, type HubStage, type MemberAnnouncement } from './memberHubLogic'

function prefersReducedMotion(): boolean {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches } catch { return false }
}

type Point = { x: number; y: number }
const BURST = Array.from({ length: 10 }, (_, i) => {
  const angle = (Math.PI * 2 * i) / 10
  const radius = 118 + (i % 3) * 22
  return { dx: Math.round(Math.cos(angle) * radius), dy: Math.round(Math.sin(angle) * radius), size: 10 + (i % 4) * 4, delay: (0.7 + (i % 4) * 0.06).toFixed(2) }
})

function centerOf(el: Element | null | undefined): Point | null {
  if (!el) return null
  const box = el.getBoundingClientRect()
  return box.width || box.height ? { x: box.left + box.width / 2, y: box.top + box.height / 2 } : null
}

/** ตำแหน่งปุ่มถุงทองลอยมุมขวาล่าง (ตรงกับ .mh-fab ใน member-hub.css) — ใช้เมื่อยังไม่มีปุ่มบนจอ */
function cornerPoint(): Point {
  const right = Math.max(10, (window.innerWidth - 480) / 2 + 10)
  return { x: window.innerWidth - right - 32, y: window.innerHeight - 20 - 35 }
}

/**
 * ศูนย์สมาชิกบนหน้า "ออร์เดอร์ของฉัน" (ไม่แสดงในโหมดแอดมินดูมุมมองลูกค้า)
 * ป้ายประกาศ → ปุ่มถุงทองลอยมุมขวาล่าง (ไม่มีวงกลมพื้นหลัง · ประกายทองรอบตัว)
 * กิจกรรมใหม่ (แบบ C) = กล่องของขวัญลอยเกาะข้างถุงทอง ไม่มีป๊อปอัพบังหน้า → ลูกค้าแตะเอง:
 *   กล่องเปิดกลางจอ น้ำเต้าลอยขึ้น → แตะน้ำเต้า = การ์ดกิจกรรมพุ่งออกจากน้ำเต้า (เอฟเฟกต์จินนี่) · "ไว้ทีหลัง" = น้ำเต้าบินกลับมุมจอ
 * ไม่มีกิจกรรมใหม่ = แตะถุงทองที่มุม การ์ดพุ่งออกจากถุงทองทันที
 * ข้อมูลจากแคชไม่มีป้าย/สถานะใหม่ (memberSession.cacheableMemberData) จึงเริ่มลำดับเมื่อข้อมูลสดมาถึงเท่านั้น
 * ป้ายที่มาถึงทีหลัง (รีเฟรชตอนกดถุงทอง/กลับมาที่แท็บ) ขึ้นได้ระหว่างเปิดหน้า — เฉพาะป้ายที่ยังไม่เคยขึ้น
 * เปิดรายการกิจกรรม = เรียก onActivitiesOpen ให้ดึงข้อมูลสด (กิจกรรม/ประกาศที่แอดมินเพิ่งบันทึกขึ้นทันที)
 */
export function MemberHub({ announcements: rawAnn, activities: rawAct, hasNew, benefitsEligible, botId, onGoOrders, onProfileRequest, onActivitiesOpen, openRequest = 0 }: {
  announcements: unknown
  activities: unknown
  hasNew: boolean
  benefitsEligible: boolean
  botId: string
  onGoOrders: () => void
  onProfileRequest: () => void
  onActivitiesOpen?: () => void
  openRequest?: number
}) {
  const announcements = readAnnouncements(rawAnn)
  const activities = readActivities(rawAct)
  const [stage, setStage] = useState<HubStage>('none')
  const [deck, setDeck] = useState<MemberAnnouncement[]>([])
  const [sheetOpen, setSheetOpen] = useState(false)
  const [seenNew, setSeenNew] = useState(false)
  const [popFab, setPopFab] = useState(false)
  const [genieFrom, setGenieFrom] = useState<Point | null>(null)
  const [hint, setHint] = useState(false)
  const startedRef = useRef(false)
  const shownSignatureRef = useRef('')
  const shownAnnRef = useRef(new Set<string>())
  const lastOpenRequestRef = useRef(0)
  const restoreFabFocusRef = useRef(false)
  const bagRef = useRef<HTMLDivElement>(null)
  const bagDialogRef = useRef<HTMLElement>(null)
  const fabRef = useRef<HTMLButtonElement>(null)
  const hasActivities = activities.length > 0
  const newActivities = activities.filter((activity) => activity.isNew)
  const newSignature = hasNew ? (newActivities.length ? newActivities : activities)
    .map((activity) => `${activity.id}:${activity.version || 1}`).sort().join('|') : ''
  const pending = hasNew && !seenNew
  const gift = giftWaiting(hasNew, seenNew, benefitsEligible)
  const annSignature = announcements.map(announcementKey).join('|')

  /** ขึ้นป้ายชุดนี้ + จำว่าขึ้นแล้ว (กันเด้งซ้ำเมื่อข้อมูลรีเฟรช) */
  const takeDeck = (list: MemberAnnouncement[]) => {
    const fresh = unshownAnnouncements(list, shownAnnRef.current)
    fresh.forEach((a) => shownAnnRef.current.add(announcementKey(a)))
    if (fresh.length) setDeck(fresh)
    return fresh
  }

  useEffect(() => {
    if (startedRef.current) return
    if (!benefitsEligible) { setStage('fab'); return }
    if (announcements.length || hasNew) {
      startedRef.current = true
      shownSignatureRef.current = newSignature
      setStage(initialStage(takeDeck(announcements)))
    } else {
      setStage('fab')
    }
    // เริ่มลำดับครั้งเดียวต่อการเปิดหน้า
  }, [announcements.length, hasNew, newSignature, benefitsEligible]) // eslint-disable-line react-hooks/exhaustive-deps

  // ป้ายใหม่ที่มาจากการรีเฟรช — รอให้ลูกค้าปิดป๊อปอัพที่เปิดอยู่ก่อน
  useEffect(() => {
    if (!benefitsEligible || !startedRef.current || !annSignature || sheetOpen || stage === 'announcements' || stage === 'gift') return
    if (takeDeck(announcements).length) setStage('announcements')
  }, [annSignature, stage, sheetOpen, benefitsEligible]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!benefitsEligible || !startedRef.current || !newSignature || newSignature === shownSignatureRef.current || stage === 'announcements') return
    shownSignatureRef.current = newSignature
    // กำลังดูรายการกิจกรรม/เปิดกล่องอยู่ = เห็นกิจกรรมใหม่แล้ว ไม่ต้องขึ้นกล่องของขวัญซ้ำ · นอกนั้นกล่องจะลอยขึ้นข้างถุงทองที่มุมจอเอง
    if (sheetOpen || stage === 'gift') { markSeen(); return }
    setSeenNew(false)
  }, [newSignature, stage, benefitsEligible, sheetOpen]) // eslint-disable-line react-hooks/exhaustive-deps



  useEffect(() => {
    if (stage === 'gift') {
      const previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = previousOverflow }
    }
    if (stage === 'fab' && restoreFabFocusRef.current) {
      restoreFabFocusRef.current = false
      fabRef.current?.focus()
    }
  }, [stage])

  const markSeen = useCallback(() => {
    if (!benefitsEligible) return
    setSeenNew(true)
    restoreFabFocusRef.current = true
    const flagged = activities.filter((activity) => activity.isNew)
    const fresh = flagged.length ? flagged : hasNew ? activities : []
    if (fresh.length) void sendMemberUiEvent({ event: 'activities_seen', entries: fresh.map((activity) => ({ id: activity.id, version: activity.version || 1 })) })
  }, [activities, hasNew, benefitsEligible])

  const onAnnouncementsClosed = () => {
    const next = afterAnnouncements()
    if (prefersReducedMotion()) setStage(next)
    else window.setTimeout(() => setStage(next), 260)
  }

  /** เปิดรายการกิจกรรม — การ์ดพุ่งออกจาก from (น้ำเต้ากลางจอ/ปุ่มที่มุม) */
  const openSheet = (from?: Point | null) => {
    setGenieFrom(from || centerOf(fabRef.current) || cornerPoint())
    setSheetOpen(true)
    onActivitiesOpen?.()
    if (pending) markSeen()
  }

  const onFabClick = () => {
    if (gift) { setStage('gift'); return }
    openSheet(centerOf(fabRef.current))
  }

  const openFromGift = () => {
    const from = centerOf(bagRef.current)
    setStage('fab')
    openSheet(from)
  }

  useEffect(() => {
    if (openRequest <= lastOpenRequestRef.current) return
    lastOpenRequestRef.current = openRequest
    setStage('fab')
    setGenieFrom(centerOf(fabRef.current) || cornerPoint())
    setSheetOpen(true)
    onActivitiesOpen?.()
    if (pending) markSeen()
  }, [openRequest, pending, markSeen, onActivitiesOpen])

  const later = () => {
    markSeen()
    const bag = bagRef.current
    const finish = () => {
      setStage('fab')
      setPopFab(true)
      setHint(true)
      window.setTimeout(() => setHint(false), 2600)
    }
    if (!bag || prefersReducedMotion() || typeof bag.animate !== 'function') { finish(); return }
    const from = bag.getBoundingClientRect()
    const size = 64
    // ต้องตรงกับ .mh-fab ใน member-hub.css: ชิดขอบ "จอมือถือ" (คอลัมน์สูงสุด 480px) ไม่ใช่ขอบเบราว์เซอร์
    const right = Math.max(10, (window.innerWidth - 480) / 2 + 10)
    const bottom = 20
    const toX = window.innerWidth - right - size / 2
    const toY = window.innerHeight - bottom - size / 2
    const flyer = bag.cloneNode(true) as HTMLElement
    flyer.className = 'mh-bag-flyer'
    Object.assign(flyer.style, { left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, height: `${from.height}px` })
    document.body.appendChild(flyer)
    setStage('none')
    const dx = toX - (from.left + from.width / 2)
    const dy = toY - (from.top + from.height / 2)
    const s = (size / from.width) * 0.9
    const anim = flyer.animate([
      { transform: 'translate(0,0) scale(1) rotate(0)' },
      { transform: `translate(${dx * 0.45}px, ${dy * 0.45 - 70}px) scale(${(1 + s) / 2}) rotate(-14deg)`, offset: 0.5 },
      { transform: `translate(${dx}px, ${dy}px) scale(${s}) rotate(0)` },
    ], { duration: 780, easing: 'cubic-bezier(.45,0,.3,1)' })
    const done = () => { flyer.remove(); finish() }
    anim.onfinish = done
    anim.oncancel = done
  }

  const onBagKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') { event.preventDefault(); later(); return }
    if (event.key !== 'Tab') return
    const buttons = bagDialogRef.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])')
    if (!buttons?.length) return
    if (event.shiftKey && document.activeElement === buttons[0]) { event.preventDefault(); buttons[buttons.length - 1].focus() }
    else if (!event.shiftKey && document.activeElement === buttons[buttons.length - 1]) { event.preventDefault(); buttons[0].focus() }
  }

  return createPortal((
    <>
      {stage === 'announcements' && deck.length > 0 && (
        <AnnouncementPopup deck={deck} botId={botId} onClosed={onAnnouncementsClosed} onGoOrders={onGoOrders} />
      )}

      {stage === 'gift' && (
        <div className="mh-ov mh-ov--gift" onMouseDown={(e) => { if (e.target === e.currentTarget) later() }}>
          <section ref={bagDialogRef} className="mh-gift" role="dialog" aria-modal="true" aria-labelledby="mh-gift-title" onKeyDown={onBagKeyDown}>
            <span className="mh-gift-glow" aria-hidden="true" />
            <span className="mh-gift-rays" aria-hidden="true" />
            {BURST.map((b, i) => (
              <span key={i} className="mh-gift-burst" aria-hidden="true" style={{ '--dx': `${b.dx}px`, '--dy': `${b.dy}px`, '--s': `${b.size}px`, '--d': `${b.delay}s` } as CSSProperties}>✦</span>
            ))}
            <div className="mh-gift-box" aria-hidden="true">
              <GiftBoxIcon className="mh-gift-body" part="body" />
              <GiftBoxIcon className="mh-gift-lid" part="lid" />
            </div>
            <div className="mh-gift-bag" ref={bagRef}>
              <button type="button" className="mh-gift-bagbtn" onClick={openFromGift} aria-label="เปิดดูกิจกรรมในถุงทอง" autoFocus>
                <GoldBagIcon animated />
              </button>
            </div>
            <span className="mh-gift-floor" aria-hidden="true" />
            <h2 id="mh-gift-title">คุณได้รับถุงทอง!</h2>
            <p>{newActivities.length || activities.length} กิจกรรมใหม่ · แตะน้ำเต้าเพื่อดูรายละเอียด</p>
            <button type="button" className="mh-textbtn" onClick={later}>ไว้ทีหลัง</button>
          </section>
        </div>
      )}

      {stage === 'fab' && (
        <>
          <button
            ref={fabRef}
            type="button"
            className={`mh-fab${popFab ? ' is-pop' : ''}${pending ? ' is-new' : ''}${hasActivities ? ' has-acts' : ''}`}
            onClick={onFabClick}
            aria-label={gift ? `เปิดกล่องของขวัญ มีกิจกรรมใหม่ ${newActivities.length || activities.length} รายการ` : hasActivities ? `เปิดถุงทอง กิจกรรมสำหรับคุณ ${activities.length} รายการ` : 'เปิดถุงทอง ยังไม่มีกิจกรรม'}
          >
            <span className="mh-fab-shadow" aria-hidden="true" />
            <GoldBagIcon animated />
            {/* กล่องของขวัญเกาะข้างถุงทอง = มีกิจกรรมใหม่ที่ยังไม่เคยเห็น */}
            {gift && <span className="mh-fab-gift" aria-hidden="true"><GiftBoxIcon /></span>}
            {gift && <span className="mh-fab-label" aria-hidden="true">เปิดเพื่อรับรางวัล</span>}
            {/* ประกายทองรอบถุงทอง — แสดงเสมอ (ลดการเคลื่อนไหว/ประหยัดพลังงาน = ประกายนิ่ง) */}
            {[1, 2, 3, 4, 5].map((n) => <span key={n} className={`mh-fab-spark mh-fab-spark--${n}`} aria-hidden="true">✦</span>)}
            {hasActivities && <span className="mh-fab-badge" aria-hidden="true">{activities.length > 9 ? '9+' : activities.length}</span>}
          </button>
          {hint && !gift && <div className="mh-fab-hint" role="status">กิจกรรมของคุณอยู่ตรงนี้</div>}
        </>
      )}

      {sheetOpen && <ActivitySheet activities={activities.map((activity) => ({ ...activity, isNew: activity.isNew && !seenNew }))} botId={botId} benefitsEligible={benefitsEligible} onProfileRequest={() => { setSheetOpen(false); onProfileRequest() }} onClose={() => { setSheetOpen(false); fabRef.current?.focus() }} genieFrom={genieFrom} />}
    </>
  ), document.body)
}
